import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../../api/client';
import { CATEGORIES, money } from './FeeRuleForm';

// Fees & Taxes → Payment methods / Cash fees & offsets (Kayd, 28.10.2026).
//   Payment methods: which methods NULA-MOVE approves per country and
//   category. Without an entry only online payment is possible (as before).
//   Providers can only switch on approved methods for their listings.
//   Cash fees & offsets: NULA-MOVE fees owed by providers from cash bookings,
//   their automatic offsets against later online payouts and what is open.
const errText = (e, fallback) => e?.response?.data?.error || fallback;
const CAT_LABEL = CATEGORIES;
const day = (iso) => (iso ? new Date(iso).toLocaleDateString() : '—');

export function PaymentMethodApprovals({ scopeCountry }) {
  const [rows, setRows] = useState(null);
  const [setupNeeded, setSetupNeeded] = useState(false);
  const [form, setForm] = useState({ country_code: scopeCountry || '', category: 'hotel', online_allowed: true, cash_allowed: false, note: '' });
  const [saving, setSaving] = useState(false);
  const load = useCallback(() => {
    api.get('/api/rooms/admin/payment-methods').then((r) => { setRows(r.data?.approvals || []); setSetupNeeded(!!r.data?.setup_needed); })
      .catch((e) => { setRows([]); toast.error(errText(e, 'Could not load payment methods')); });
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (scopeCountry) setForm((f) => ({ ...f, country_code: scopeCountry })); }, [scopeCountry]);

  const save = async (e) => {
    e.preventDefault();
    if (!form.note.trim()) { toast.error('Please enter the reason (saved in the log).'); return; }
    setSaving(true);
    try {
      await api.put('/api/rooms/admin/payment-methods', form);
      toast.success('Saved');
      setForm((f) => ({ ...f, note: '' }));
      load();
    } catch (err) {
      toast.error(errText(err, 'Could not save'));
    } finally {
      setSaving(false);
    }
  };
  const edit = (r) => setForm({ country_code: r.country_code, category: r.category, online_allowed: r.online_allowed, cash_allowed: r.cash_allowed, note: '' });
  const visible = (rows || []).filter((r) => !scopeCountry || r.country_code === scopeCountry);
  const cats = Object.entries(CAT_LABEL);

  return (
    <div>
      <div style={s.infoBox}>
        Approve which payment methods providers may offer, per country and category. <strong>Without an entry only online payment is possible</strong> (as before).
        Providers choose from the approved methods for each listing; customers then pick one when booking. With <strong>cash on arrival</strong> the customer pays the full amount
        (incl. the NULA-MOVE fee) to the provider; the NULA-MOVE fee becomes owed by the provider only after check-in and cash receipt are both confirmed, and is offset against the provider's later online payouts.
        Today cash on arrival works for hotels, vacation rentals, wedding halls and event venues; vehicle rentals still pay online.
      </div>
      {setupNeeded && <div style={s.warnBox}>The database update for payment methods has not been run yet.</div>}
      <form onSubmit={save} style={s.form}>
        <div style={s.grid}>
          <label style={s.label}>Country (code)
            <input style={s.input} value={form.country_code} onChange={(e) => setForm({ ...form, country_code: e.target.value.toUpperCase() })} maxLength={3} placeholder="KE" required />
          </label>
          <label style={s.label}>Category
            <select style={s.input} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
              {cats.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </label>
        </div>
        <div style={s.checks}>
          <label style={s.check}><input type="checkbox" checked={form.online_allowed} onChange={(e) => setForm({ ...form, online_allowed: e.target.checked })} /> Online payment</label>
          <label style={s.check}><input type="checkbox" checked={form.cash_allowed} onChange={(e) => setForm({ ...form, cash_allowed: e.target.checked })} /> Cash payment on arrival</label>
        </div>
        <label style={s.label}>Reason (saved in the log)
          <input style={s.input} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} maxLength={500} required />
        </label>
        <div><button type="submit" style={s.primary} disabled={saving}>{saving ? 'Saving…' : 'Save approval'}</button></div>
      </form>
      <div style={s.tableWrap}>
        <table style={s.table}>
          <thead><tr>{['Country', 'Category', 'Online', 'Cash on arrival', 'Note', 'Updated', ''].map((h) => <th key={h} style={s.th}>{h}</th>)}</tr></thead>
          <tbody>
            {rows === null ? <tr><td colSpan={7} style={s.td}>Loading…</td></tr> : visible.length === 0 ? (
              <tr><td colSpan={7} style={s.td}>No approvals yet — online payment only everywhere.</td></tr>
            ) : visible.map((r) => (
              <tr key={r.id}>
                <td style={s.td}>{r.country_code}</td>
                <td style={s.td}>{CAT_LABEL[r.category] || r.category}</td>
                <td style={s.td}>{r.online_allowed ? '✓ approved' : '—'}</td>
                <td style={s.td}>{r.cash_allowed ? '✓ approved' : '—'}</td>
                <td style={s.td}>{r.note || '—'}</td>
                <td style={s.td}>{day(r.updated_at)}</td>
                <td style={s.td}><button type="button" style={s.link} onClick={() => edit(r)}>Edit</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const REC_STATUS = { outstanding: 'Outstanding', partially_offset: 'Partially offset', fully_offset: 'Fully offset', cancelled: 'Cancelled' };

export function CashFeeBalance() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('');
  useEffect(() => {
    setData(null);
    api.get('/api/rooms/admin/fee-balance', { params: status ? { status } : {} }).then((r) => setData(r.data))
      .catch((e) => { setData({ receivables: [], totals: [] }); toast.error(errText(e, 'Could not load')); });
  }, [status]);
  return (
    <div>
      <div style={s.infoBox}>
        NULA-MOVE fees owed by providers from cash bookings (created once, after check-in <em>and</em> cash receipt were confirmed) and refunds recovered after a payout.
        They are offset automatically at the payout of the same provider's later online bookings, in the same currency, oldest first — never more than the provider share,
        never between providers or currencies. A full refund of a cash booking cancels its receivable; offsets already made are reversed by a separate entry.
      </div>
      <div style={s.toolbar}>
        <select style={{ ...s.input, maxWidth: 220 }} value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
          <option value="">All statuses</option>
          {Object.entries(REC_STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>
      {data && data.totals.length > 0 && (
        <div style={s.totals}>
          {data.totals.map((t) => (
            <div key={t.currency} style={s.total}>
              <strong>{t.currency}</strong>
              <div style={s.muted}>Owed {money(t.original_cents, t.currency)} · offset {money(t.offset_cents, t.currency)} · open {money(t.remaining_cents, t.currency)}{t.reversed_cents ? ` · reversed ${money(t.reversed_cents, t.currency)}` : ''}</div>
            </div>
          ))}
        </div>
      )}
      <div style={s.tableWrap}>
        <table style={s.table}>
          <thead><tr>{['Receivable', 'Provider', 'Original booking', 'Cash received', 'Amount', 'Offset', 'Open', 'Status', 'Offsets'].map((h) => <th key={h} style={s.th}>{h}</th>)}</tr></thead>
          <tbody>
            {!data ? <tr><td colSpan={9} style={s.td}>Loading…</td></tr> : data.receivables.length === 0 ? <tr><td colSpan={9} style={s.td}>No fee receivables.</td></tr> : data.receivables.map((r) => (
              <tr key={r.id}>
                <td style={s.td}>{r.receivable_number}<div style={s.muted}>{r.kind === 'payout_reversal' ? 'Refund after payout' : 'Cash booking fee'} · {day(r.created_at)}</div></td>
                <td style={s.td}>{r.provider?.full_name || '—'}<div style={s.muted}>#{r.provider?.customer_number || '—'}</div></td>
                <td style={s.td}>{r.booking?.listing?.title || '—'}<div style={s.muted}>{r.booking ? `${r.booking.check_in} → ${r.booking.check_out}` : ''}</div></td>
                <td style={s.td}>{r.booking?.cash_received_at ? <>{money(r.booking.cash_received_cents ?? r.booking.total_price_cents, r.currency)}<div style={s.muted}>{day(r.booking.cash_received_at)}{r.booking.checkin_confirmed_at ? ` · check-in ${day(r.booking.checkin_confirmed_at)}` : ''}</div></> : '—'}</td>
                <td style={s.td}>{money(r.amount_cents, r.currency)}</td>
                <td style={s.td}>{money(r.offset_cents, r.currency)}</td>
                <td style={s.td}><strong>{money(r.remaining_cents, r.currency)}</strong></td>
                <td style={s.td}><span style={s.badge}>{REC_STATUS[r.status] || r.status}</span>{r.cancel_reason && <div style={s.muted}>{r.cancel_reason}</div>}</td>
                <td style={s.td}>
                  {r.offsets.length === 0 ? '—' : r.offsets.map((o) => (
                    <div key={o.id} style={s.muted}>
                      {day(o.created_at)} · {o.entry_type === 'reversal' ? 'reversed' : 'offset'} {money(o.amount_cents, o.currency)} · payout of {o.payout_booking?.listing?.title || 'booking'}{o.payout_booking ? ` (${o.payout_booking.check_in})` : ''}
                    </div>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const s = {
  infoBox: { background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e3a8a', borderRadius: 10, padding: 12, fontSize: 13.5, marginBottom: 12, lineHeight: 1.5 },
  warnBox: { background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', borderRadius: 10, padding: 12, fontSize: 13.5, marginBottom: 12 },
  form: { background: '#fff', border: '1px solid #d1d5db', borderRadius: 12, padding: 16, marginBottom: 14, display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 640 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 },
  label: { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5, fontWeight: 700, color: '#374151', minWidth: 0 },
  input: { padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13.5, background: '#fff', width: '100%', boxSizing: 'border-box', minHeight: 38 },
  checks: { display: 'flex', gap: 16, flexWrap: 'wrap' },
  check: { display: 'flex', gap: 6, alignItems: 'center', fontSize: 13.5 },
  primary: { padding: '9px 14px', borderRadius: 8, border: 'none', background: '#0B6A4A', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' },
  link: { background: 'none', border: 'none', color: '#0B6A4A', fontWeight: 700, cursor: 'pointer', padding: '2px 6px', fontSize: 13 },
  toolbar: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 },
  totals: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 10, marginBottom: 12 },
  total: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 10 },
  tableWrap: { width: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10 },
  table: { width: '100%', minWidth: 900, borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '9px 10px', borderBottom: '2px solid #e5e7eb', color: '#6b7280', fontSize: 12, whiteSpace: 'nowrap' },
  td: { padding: '9px 10px', borderBottom: '1px solid #f3f4f6', verticalAlign: 'top' },
  muted: { fontSize: 12, color: '#6b7280' },
  badge: { padding: '3px 8px', borderRadius: 999, background: '#f3f4f6', fontSize: 11, fontWeight: 700, color: '#374151' },
};
